import { SdkContext } from '../internal'
import { fetchFile, postFile, queuedExportOf, type DownloadedFile, type QueuedExport } from '../fileDownload'
import type { Event, EventCategoryOption } from '@/types/events'
import type { EmailNoticeResult } from './email-notices'
import type {
  CertificatePreview, DoorCheckInResult, OrganizedEvent, DoorList, DoorListQuery, EventKiosk, MyTicketResponse, StreamAccess
} from '@/types/eventCheckIn'
import type {
  EventTickets, MyTickets, NameTagSettings, NameTagTemplate, TicketBatchInput, TicketSales, TicketTransferResult, TicketTransferRow, TicketTransferStatus, TicketType, TicketTypeInput
} from '@/types/tickets'
import type { EventRegistrationQuestions, EventRegistrations, EventRegistrationsQuery, MyEventRegistration } from '@/types/eventRegistration'
import type { FormAnswerInput, FormSection } from '@/types/forms'

export type { Event }

/** A row of `GET /events/{id}/participants`. */
export interface ParticipantRow {
  id: string
  status: 'going' | 'maybe' | 'not_going'
  registeredAt: string
  checkedInAt?: string | null
  checkInMethod?: string | null
  courtesy?: boolean
  member: { memberId: string; name: string; avatarUrl: string | null; role: string }
}

/** The create/update body: the editable event fields plus host, organizer and certificate settings. */
type EventWriteBody = Record<string, unknown>

export interface MyTicketAcross {
  id: string
  status: 'active' | 'canceled'
  code: string
  token: string | null
  qrDataUrl: string | null
  courtesy: boolean
  checkedInAt: string | null
  ticketType: { id: string; name: string; requiresProof?: boolean } | null
  /** F-122: whether the holder may pass it on now, and why not. */
  transfer?: TicketTransferStatus | null
  event: { id: string; slug: string; title: string; type: string; startTime: string | null; endTime: string | null; location: string | null; spaceId: string | null; checkInEnabled: boolean; timeZone?: string }
}

export class EventsResource {
  constructor(private ctx: SdkContext) {}

  async list(params: { spaceId?: string; page?: number; limit?: number; filter?: 'next' | 'past' | 'all' | 'my' | string; search?: string; status?: 'PUBLISHED' | 'DRAFT' | 'all'; /** F-198: only the events with no date. */ undated?: boolean } = {}): Promise<Event[]> {
    const result = await this.ctx.raw<Event[] | { items?: Event[] }>({
      httpMethod: 'GET',
      path: '/events'
    }, { query: params })
    if (Array.isArray(result)) return result
    return result?.items || []
  }

  /**
   * F-198: the events that cross a period (calendar), `from`/`to` as ISO, at most 62 days apart. No
   * pagination inside the period: the API caps it at 300 by `startTime` and says `truncated`.
   * `filter=next|past` is ignored with a period; `filter=my` and `status=DRAFT` combine with it.
   */
  async listRange(params: { spaceId?: string; from: string; to: string; filter?: 'my'; search?: string; status?: 'DRAFT'; categoryId?: string }): Promise<{ items: Event[]; truncated: boolean; undatedCount: number }> {
    /* The whole envelope: `truncated` and `undatedCount` sit beside `data`, with the pagination. */
    type Page = Event[] | { items?: Event[]; data?: Event[]; truncated?: boolean; undatedCount?: number }
    const read = (page: Page | null | undefined) => {
      if (!page) return { items: [], truncated: false, undatedCount: 0 }
      if (Array.isArray(page)) return { items: page, truncated: false, undatedCount: 0 }
      return {
        items: page.items || page.data || [],
        truncated: page.truncated === true,
        undatedCount: Number.isFinite(Number(page.undatedCount)) ? Number(page.undatedCount) : 0
      }
    }
    return read(await this.ctx.envelope<Page>({ httpMethod: 'GET', path: '/events' }, { query: params }))
  }

  /** "Meus ingressos": the viewer's own tickets across the community's events (`upcoming` with QR, or `past`). */
  async myTicketsAcross(params: { when?: 'upcoming' | 'past'; page?: number; limit?: number } = {}): Promise<{ items: MyTicketAcross[]; pagination?: { page: number; limit: number; total: number; pages: number } }> {
    const result = await this.ctx.raw<MyTicketAcross[] | { items?: MyTicketAcross[]; data?: MyTicketAcross[]; pagination?: { page: number; limit: number; total: number; pages: number } }>({ httpMethod: 'GET', path: '/my/tickets' }, { query: params })
    if (Array.isArray(result)) return { items: result }
    return { items: result?.items || result?.data || [], pagination: result?.pagination }
  }

  async get(identifier: string, params?: { communityId?: string; spaceId?: string }): Promise<Event> {
    return this.ctx.call({ httpMethod: 'GET', path: `/events/{identifier}` }, { identifier, query: params })
  }

  async create(event: EventWriteBody): Promise<Event & { emailNotice?: EmailNoticeResult | null }> {
    return this.ctx.call({ httpMethod: 'POST', path: '/events', hasBody: true }, { body: event })
  }

  async update(id: string, event: EventWriteBody): Promise<Event> {
    return this.ctx.call({ httpMethod: 'PUT', path: `/events/{id}`, hasBody: true }, { id, body: event })
  }

  /**
   * The editor's autosave: only the fields sent. `updatedAt` is the version read;
   * 409 (`errors[].param = updatedAt`) means another tab or person saved first.
   */
  async patch(id: string, event: EventWriteBody): Promise<Event> {
    return this.ctx.call({ httpMethod: 'PATCH', path: `/events/{id}`, hasBody: true }, { id, body: event })
  }

  /** Publishes a draft: 422 with `errors[].param` per missing readiness row, 409 when already published. */
  async publish(id: string, body: { notifyByEmail?: boolean } = {}): Promise<Event & { emailNotice?: EmailNoticeResult | null }> {
    return this.ctx.call({ httpMethod: 'POST', path: `/events/{id}/publish`, hasBody: true }, { id, body })
  }

  /** F-76: sets the recording to a confirmed `eventRecording` media. */
  async setRecording(id: string, mediaId: string): Promise<{ id: string; recordingMediaId: string | null }> {
    return this.ctx.call({ httpMethod: 'PUT', path: `/events/{id}/recording`, hasBody: true }, { id, body: { mediaId } })
  }

  async removeRecording(id: string): Promise<{ id: string; recordingMediaId: string | null }> {
    return this.ctx.call({ httpMethod: 'DELETE', path: `/events/{id}/recording` }, { id })
  }

  async delete(id: string): Promise<void> {
    return this.ctx.call({ httpMethod: 'DELETE', path: `/events/{id}` }, { id })
  }

  /** F-117: `going` takes the registration `answers` (422 per field; 422 `answersRequired` with `data.missing`). */
  async participate(id: string, status: string, answers?: FormAnswerInput[]): Promise<void> {
    return this.ctx.call({ httpMethod: 'POST', path: `/events/{id}/participate`, hasBody: true }, { id, body: { status, ...(answers ? { answers } : {}) } })
  }

  /* ---------------------------------------------------------------- F-116: ticket types and batches */

  /** Whoever sees the event: the types on sale with the current batch; the team, every type and batch (`permissions`). */
  async ticketTypes(id: string): Promise<EventTickets> {
    return this.ctx.call({ httpMethod: 'GET', path: `/events/{id}/ticket-types` }, { id })
  }

  async createTicketType(id: string, body: TicketTypeInput & { name: string }): Promise<TicketType> {
    return this.ctx.call({ httpMethod: 'POST', path: `/events/{id}/ticket-types`, hasBody: true }, { id, body })
  }

  async updateTicketType(id: string, typeId: string, body: TicketTypeInput): Promise<TicketType> {
    return this.ctx.call({ httpMethod: 'PATCH', path: `/events/{id}/ticket-types/{typeId}`, hasBody: true }, { id, typeId, body })
  }

  /** Without sales only (409 `tickets.typeHasSales`, `tickets.lastType`). */
  async deleteTicketType(id: string, typeId: string): Promise<{ id: string; deleted: boolean }> {
    return this.ctx.call({ httpMethod: 'DELETE', path: `/events/{id}/ticket-types/{typeId}` }, { id, typeId })
  }

  async addTicketBatch(id: string, typeId: string, body: TicketBatchInput): Promise<TicketType> {
    return this.ctx.call({ httpMethod: 'POST', path: `/events/{id}/ticket-types/{typeId}/batches`, hasBody: true }, { id, typeId, body })
  }

  /** A batch with sales keeps its price (409 `tickets.batchHasSales`): close it and open another. */
  async updateTicketBatch(id: string, typeId: string, batchId: string, body: TicketBatchInput): Promise<TicketType> {
    return this.ctx.call({ httpMethod: 'PATCH', path: `/events/{id}/ticket-types/{typeId}/batches/{batchId}`, hasBody: true }, { id, typeId, batchId, body })
  }

  async deleteTicketBatch(id: string, typeId: string, batchId: string): Promise<TicketType> {
    return this.ctx.call({ httpMethod: 'DELETE', path: `/events/{id}/ticket-types/{typeId}/batches/{batchId}` }, { id, typeId, batchId })
  }

  async updateTicketSettings(id: string, body: { capacity?: number | null; showRemaining?: boolean; subscriberTicketTypeId?: string | null; transferEnabled?: boolean; transferDeadline?: string | null }): Promise<EventTickets> {
    return this.ctx.call({ httpMethod: 'PATCH', path: `/events/{id}/ticket-settings`, hasBody: true }, { id, body })
  }

  /** Owner, admin and finance. */
  async ticketSales(id: string): Promise<TicketSales> {
    return this.ctx.call({ httpMethod: 'GET', path: `/events/{id}/ticket-sales` }, { id })
  }

  async exportTicketSales(id: string): Promise<DownloadedFile> {
    return fetchFile(this.ctx, `/events/${encodeURIComponent(id)}/ticket-sales`, { format: 'csv' }, 'text/csv', `ingressos-${id}.csv`)
  }

  /** F-116b: the tickets of the viewer's orders of this event (theirs and the ones to give). */
  async myTickets(id: string): Promise<MyTickets> {
    return this.ctx.call({ httpMethod: 'GET', path: `/events/{id}/my-tickets` }, { id })
  }

  async assignTicket(id: string, ticketId: string, body: { email: string; name?: string }): Promise<MyTickets> {
    return this.ctx.call({ httpMethod: 'POST', path: `/events/{id}/my-tickets/{ticketId}/assign`, hasBody: true }, { id, ticketId, body })
  }

  /** F-117 4: the buyer answers ahead for a ticket of their group order (no consent: that is the holder's). */
  async ticketRegistration(id: string, ticketId: string): Promise<MyEventRegistration> {
    return this.ctx.call({ httpMethod: 'GET', path: `/events/{id}/my-tickets/{ticketId}/registration` }, { id, ticketId })
  }

  async saveTicketRegistration(id: string, ticketId: string, answers: FormAnswerInput[]): Promise<MyEventRegistration> {
    return this.ctx.call({ httpMethod: 'PUT', path: `/events/{id}/my-tickets/{ticketId}/registration`, hasBody: true }, { id, ticketId, body: { answers } })
  }

  /* ---------------------------------------------------------------- F-117: registration questions */

  /** The team and the event's organizers (403 `eventAnswersForbidden`); null without questions. */
  async registrationQuestions(id: string): Promise<EventRegistrationQuestions | null> {
    return this.ctx.call({ httpMethod: 'GET', path: `/events/{id}/registration-questions` }, { id })
  }

  /** The team writes (403 `eventQuestionsForbidden`); `sections: []` removes the questions (answers stay). */
  async setRegistrationQuestions(id: string, body: { sections: FormSection[]; privacyPolicyUrl?: string | null }): Promise<EventRegistrationQuestions | null> {
    return this.ctx.call({ httpMethod: 'PUT', path: `/events/{id}/registration-questions`, hasBody: true }, { id, body })
  }

  /** APP-154: with `ticketTypeId`, the questions of that ticket type, before buying. */
  async myRegistration(id: string, ticketTypeId?: string): Promise<MyEventRegistration> {
    return this.ctx.call({ httpMethod: 'GET', path: `/events/{id}/my-registration` }, { id, ...(ticketTypeId ? { query: { ticketTypeId } } : {}) })
  }

  /** Until the event starts (409 `notGoing` / `eventStarted`); 422 per field. */
  async saveMyRegistration(id: string, answers: FormAnswerInput[]): Promise<MyEventRegistration> {
    return this.ctx.call({ httpMethod: 'PUT', path: `/events/{id}/my-registration`, hasBody: true }, { id, body: { answers } })
  }

  /** The team and this event's organizers; the whole envelope (`pagination` beside `data`). */
  async registrations(id: string, query: EventRegistrationsQuery = {}): Promise<EventRegistrations> {
    const body = await this.ctx.envelope<{ data: Omit<EventRegistrations, 'pagination'>; pagination?: EventRegistrations['pagination'] }>(
      { httpMethod: 'GET', path: `/events/{id}/registrations` },
      { id, query }
    )
    return { ...body.data, pagination: body.pagination }
  }

  async exportRegistrations(id: string, query: Omit<EventRegistrationsQuery, 'page' | 'limit'> = {}): Promise<DownloadedFile> {
    return fetchFile(this.ctx, `/events/${encodeURIComponent(id)}/registrations/export`, query, 'text/csv', `inscricoes-${id}.csv`)
  }

  async getCategories(): Promise<EventCategoryOption[]> {
    return this.ctx.call({ httpMethod: 'GET', path: '/events/categories' })
  }

  async getParticipants(id: string, status?: string): Promise<unknown> {
    return this.ctx.call(
      { httpMethod: 'GET', path: `/events/{id}/participants` },
      { id, query: status ? { status } : undefined }
    )
  }

  /**
   * The participants with the envelope's extras: `checkIn` (F-115) sits next to `data`, and the unwrapping
   * client drops it. Presence fields come filled for the team and the event's organizers only.
   */
  async participantsWithPresence(id: string): Promise<{ participants: ParticipantRow[]; checkIn: { enabled: boolean; present: number | null } }> {
    const body = await this.ctx.envelope<{ data?: ParticipantRow[]; checkIn?: { enabled: boolean; present: number | null } }>(
      { httpMethod: 'GET', path: `/events/{id}/participants` },
      { id, query: { limit: 200 } }
    )
    return { participants: Array.isArray(body?.data) ? body.data : [], checkIn: body?.checkIn ?? { enabled: false, present: null } }
  }

  async startLivestream(id: string, payload: { streamingUrl: string; streamingService: string }): Promise<Event> {
    return this.ctx.call({ httpMethod: 'POST', path: `/events/{id}/start-stream`, hasBody: true }, { id, body: payload })
  }

  async stopLivestream(id: string): Promise<Event> {
    return this.ctx.call({ httpMethod: 'POST', path: `/events/{id}/stop-stream` }, { id })
  }

  /* ---------------------------------------------------------------- F-115: check-in */

  /** Events of this community the caller organizes, whatever the space access (F-115); unfinished first. */
  async organizing(limit = 50): Promise<{ totalCount: number; events: OrganizedEvent[] }> {
    return this.ctx.call({ httpMethod: 'GET', path: '/events/organizing' }, { query: { limit } })
  }

  /** The signed-in person's ticket (QR as PNG data URL + door code). 404 without a "going". */
  async myTicket(id: string): Promise<MyTicketResponse> {
    return this.ctx.call({ httpMethod: 'GET', path: `/events/{id}/my-ticket` }, { id })
  }

  /** The door list (team and the event's organizers). The whole envelope: `data` is an object next to `pagination`. */
  async doorList(id: string, query: DoorListQuery = {}): Promise<DoorList> {
    const body = await this.ctx.envelope<{ data: Omit<DoorList, 'pagination'>; pagination?: DoorList['pagination'] }>(
      { httpMethod: 'GET', path: `/events/{id}/check-in` },
      { id, query }
    )
    return { ...body.data, pagination: body.pagination }
  }

  /** One of `token` (QR), `code`, `ticketId` or `memberId` (+ `courtesy`, owner and admin). */
  async checkIn(id: string, body: { token?: string; code?: string; ticketId?: string; memberId?: string; courtesy?: boolean }): Promise<DoorCheckInResult> {
    return this.ctx.call({ httpMethod: 'POST', path: `/events/{id}/check-in`, hasBody: true }, { id, body })
  }

  async undoCheckIn(id: string, ticketId: string): Promise<DoorCheckInResult> {
    return this.ctx.call({ httpMethod: 'DELETE', path: `/events/{id}/check-in/{ticketId}` }, { id, ticketId })
  }

  async exportCheckIn(id: string): Promise<DownloadedFile> {
    return fetchFile(this.ctx, `/events/${encodeURIComponent(id)}/check-in/export`, {}, 'text/csv', `check-in-${id}.csv`)
  }

  async listKiosks(id: string): Promise<{ kiosks: EventKiosk[] }> {
    return this.ctx.call({ httpMethod: 'GET', path: `/events/{id}/check-in/kiosks` }, { id })
  }

  /** The kiosk URL comes only in this answer. */
  async createKiosk(id: string, name: string): Promise<{ kiosk: EventKiosk; url: string }> {
    return this.ctx.call({ httpMethod: 'POST', path: `/events/{id}/check-in/kiosks`, hasBody: true }, { id, body: { name } })
  }

  async revokeKiosk(id: string, kioskId: string): Promise<{ kiosk: EventKiosk }> {
    return this.ctx.call({ httpMethod: 'POST', path: `/events/{id}/check-in/kiosks/{kioskId}/revoke` }, { id, kioskId })
  }

  /** The live player's link; opening it while live records the online attendance (criterion 7a). */
  async streamAccess(id: string): Promise<StreamAccess> {
    return this.ctx.call({ httpMethod: 'POST', path: `/events/{id}/stream/access` }, { id })
  }

  /** Owner and admin: who the automatic certificate goes to, and who is skipped and why. */
  async certificatePreview(id: string): Promise<CertificatePreview> {
    return this.ctx.call({ httpMethod: 'GET', path: `/events/{id}/certificate-preview` }, { id })
  }

  /** F-225: the event's calendar invite (`.ics`), for whoever is going. */
  async ics(id: string): Promise<DownloadedFile> {
    return fetchFile(this.ctx, `/events/${encodeURIComponent(id)}/ics`, {}, 'text/calendar', `event-${id}.ics`)
  }

  /** F-122: the holder passes their own ticket on (irreversible; e-mails both people). */
  async transferMyTicket(ticketId: string, body: { name?: string; email: string }): Promise<TicketTransferResult> {
    return this.ctx.call({ httpMethod: 'POST', path: `/my/tickets/{ticketId}/transfer`, hasBody: true }, { ticketId, body })
  }

  /** F-122: the event's ticket transfers (team and the event's organizers). */
  async ticketTransfers(id: string): Promise<TicketTransferRow[]> {
    const result = await this.ctx.call<TicketTransferRow[] | { items?: TicketTransferRow[] }>({ httpMethod: 'GET', path: `/events/{id}/ticket-transfers` }, { id })
    return Array.isArray(result) ? result : result?.items ?? []
  }

  /** F-120: the event's name tag model (door staff). */
  async nameTag(id: string): Promise<NameTagSettings> {
    return this.ctx.call({ httpMethod: 'GET', path: `/events/{id}/name-tag` }, { id })
  }

  async saveNameTag(id: string, body: Partial<NameTagTemplate>): Promise<NameTagSettings> {
    return this.ctx.call({ httpMethod: 'PUT', path: `/events/{id}/name-tag`, hasBody: true }, { id, body })
  }

  /** F-120: a sample name tag (PDF) with the model on screen, nothing saved. */
  async nameTagPreview(id: string, body: Partial<NameTagTemplate>): Promise<DownloadedFile> {
    return postFile(this.ctx, `/events/${encodeURIComponent(id)}/name-tag/preview`, body, 'application/pdf', `name-tag-${id}.pdf`)
  }

  /** F-120: one ticket's name tag (PDF), for "Imprimir crachá"; never touches the check-in. */
  async nameTagPdf(id: string, ticketId: string): Promise<DownloadedFile> {
    return fetchFile(this.ctx, `/events/${encodeURIComponent(id)}/name-tags/${encodeURIComponent(ticketId)}`, {}, 'application/pdf', `name-tag-${ticketId}.pdf`)
  }

  /** F-120: every name tag of the event through the export queue (202); the link goes by e-mail. */
  async exportNameTags(id: string, body: { ticketTypeId?: string | null; notCheckedIn?: boolean }): Promise<QueuedExport> {
    const result = await this.ctx.call<unknown>({ httpMethod: 'POST', path: `/events/{id}/name-tags/export`, hasBody: true }, { id, body })
    return queuedExportOf(result) ?? (result as QueuedExport)
  }
}
